import { isAdvisorSourcedField } from '@bmo/form-core';

import type { MeResponse } from '@/auth/useSession';

import { isAdvisorWritable } from './bmoStatus';

/**
 * Resolves a field's `sourceValue` key against the signed-in advisor's profile.
 *
 * The key set here MUST stay in lock-step with the domain allow-list
 * (`WorkflowField.IsValidSourceValue`) and the originator's picker in
 * `FieldPropertiesPanel`. An unknown key returns null and the runtime silently
 * leaves the field empty, which reads as "prefill mysteriously stopped
 * working" — the domain is what stops a bad key ever reaching here.
 *
 * Full name + email come off the Entra/ISAM session; first/last name, phone,
 * dealer code and rep codes come off the GAMX advisor-info lookup performed at
 * ISAM login, so they are absent for originator/approver sessions and for
 * advisors whose lookup failed. Returning null in that case is deliberate: it
 * is the "nothing to say" answer, and {@link decideAdvisorPrefill} leaves the
 * slot untouched on it — so a missing profile never blanks a value that is
 * already there, even on the slots the live session otherwise overwrites.
 *
 * @returns the value to prefill, or null for "nothing to fill".
 */
export function resolveAdvisorSourceValue(key: string, user: MeResponse): string | null {
  switch (key) {
    case 'advisor.fullName':
      return user.name ?? null;
    case 'advisor.firstName':
      return user.firstName ?? null;
    case 'advisor.lastName':
      return user.lastName ?? null;
    case 'advisor.email':
      return user.email ?? null;
    case 'advisor.phone':
      return profilePhone(user.phoneNumber);
    case 'advisor.dealerCode':
      return user.dealerCode ?? null;
    case 'advisor.repCode':
      // Only auto-fill when the advisor has exactly ONE rep code. With
      // several, AC3 says the advisor picks — FieldRenderer swaps the
      // read-only input for a Select, and prefilling here would silently
      // decide for them (the slot would be non-empty, so the picker would
      // open on an arbitrary code that the advisor never chose).
      return user.representativeCodes?.length === 1 ? (user.representativeCodes[0] ?? null) : null;
    default:
      return null;
  }
}

/**
 * The profile phone as the form's own phone check accepts it (Non-Reg defect tracker #17: a
 * pre-filled rep phone failed "Please enter a valid mobile number" and the advisor could not
 * change it). Only a number that would FAIL is reshaped — a leading country code (`+1`, `1`) or a
 * trailing extension is dropped, giving "416-555-0142". A number that already passes is returned
 * exactly as stored, because that string is what the PDFs print; anything else is left as is.
 */
export function profilePhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (/^[0-9]{10}$/.test(raw.replace(/[\s\-().]/g, ''))) return raw;
  const m =
    /^\s*(?:\+?1[\s\-.]*)?\(?(\d{3})\)?[\s\-.]*(\d{3})[\s\-.]*(\d{4})(?:\s*(?:x|ext\.?|extension)\s*\d+)?\s*$/i.exec(
      raw,
    );
  return m ? `${m[1]}-${m[2]}-${m[3]}` : raw;
}

/** The field properties that decide how a slot is prefilled. */
export type AdvisorPrefilledField = {
  sourceValue?: string | null;
  advisorRepCodeSelector?: boolean | null;
  readOnly?: boolean | null;
};

/**
 * The prefill key a field draws from, or null when it draws from the session
 * not at all. An `advisorRepCodeSelector` carries no `sourceValue` of its own —
 * it is a distinct SingleSelect property — but its rules are the
 * `advisor.repCode` rules, so it borrows that key rather than duplicating them.
 *
 * Gated on form-core's {@link isAdvisorSourcedField} rather than re-testing the
 * two properties here. That predicate is also what withholds these fields from
 * the public customer runtime, and "which fields are advisor-sourced" has to be
 * ONE answer: a field this function prefills but that one does not recognise
 * would render to a customer with no session to fill it from.
 */
export function advisorSourceKeyOf(field: AdvisorPrefilledField): string | null {
  if (!isAdvisorSourcedField(field)) return null;
  if (field.advisorRepCodeSelector) return 'advisor.repCode';
  // 'application.language' is not the advisor's profile — FormShell fills it from the Welcome page.
  // It is already excluded by the predicate above; the non-null assertion is what that buys.
  return field.sourceValue!;
}

/**
 * What the session-prefill pass should do with one advisor-sourced slot:
 * write the live session's value, empty it out, or leave what is there alone.
 */
export type AdvisorPrefillAction =
  { kind: 'set'; value: string } | { kind: 'clear' } | { kind: 'leave' };

const LEAVE: AdvisorPrefillAction = { kind: 'leave' };

/** One slot's current state, as the prefill pass finds it. */
export type AdvisorPrefillSlot = {
  current: unknown;
  /**
   * The advisor has typed into (or picked in) this exact slot during THIS
   * mount — FormShell's `editedFieldsRef`. Only meaningful for the editable
   * prefills; a read-only field renders disabled and emits no change.
   */
  edited?: boolean;
};

/** Where the application stands when the prefill pass runs. */
export type AdvisorPrefillContext = {
  mode: 'preview' | 'advisor';
  /** The application's `bmoStatus` as the shell currently holds it — always a
   *  real value, seeded by `initialStatusFor` well before BMO confirms one. */
  status: string;
};

/**
 * Decide what an advisor-sourced slot should hold, given the SIGNED-IN
 * advisor's live profile and whatever the slot currently holds.
 *
 * The whole point is that the live session outranks anything restored from a
 * saved application. A resumed draft arrives carrying the advisor details as
 * they were at the last save (see `restoreValuesFromSnapshot`), and those
 * details are not facts about the form — they are facts about the person
 * filling it in, which GAMX may have since corrected (a name change, a new
 * phone, a re-coded dealer) and which may not even describe the person now
 * sitting in front of it. A "fill only the empty slots" rule quietly files the
 * application under stale identity, so these slots are re-resolved on every
 * visit instead.
 *
 * ## Only while the application is still the advisor's to move
 *
 * Refreshing stops the moment the status leaves our ladder's writable range —
 * SUBMITTED, and every BMO-owned status (`ESIGN-*`, `CLOSED`, `APP-DELETED`).
 * Past that point the application has been FILED: the screen is a record of
 * what went to BMO, not a form, and rewriting it would show the current
 * advisor's details over the ones the submission actually carried. The gate is
 * {@link isAdvisorWritable}, so it reads the same statuses the shell already
 * locks the form on — one vocabulary, not a second list to keep in step.
 *
 * ## What the advisor OWNS is still theirs
 *
 *  - **Their typing, this session.** An editable prefill is refreshed like any
 *    other slot, because on a resume nothing distinguishes a value the advisor
 *    corrected from one the prefill wrote — the snapshot records no provenance,
 *    so the live profile is the better guess. Within a mount we DO know, and
 *    `edited` is that knowledge: once they touch the field it is theirs, and a
 *    later status change re-running this pass must not take it back.
 *  - **A missing datum never blanks anything.** An originator/approver session,
 *    or an advisor whose GAMX lookup failed, resolves to null and the slot
 *    keeps whatever it had.
 *  - **A rep code is a CHOICE (AC3), not a lookup** — see the branch below.
 *
 * `mode='preview'` opts out of the rewriting half entirely and fills empty
 * slots only. Preview's values are the originator's own sample fixture
 * (PreviewPage seeds `initialValues` from `sample-values.json`), and the
 * session driving it belongs to an originator/approver, not to the advisor the
 * fixture depicts — rewriting there would replace the sample identity with the
 * reviewer's own. Same reasoning, and the same argument, as
 * {@link repCodeOptionsFor}.
 */
export function decideAdvisorPrefill(
  field: AdvisorPrefilledField,
  user: MeResponse,
  slot: AdvisorPrefillSlot,
  context: AdvisorPrefillContext,
): AdvisorPrefillAction {
  const key = advisorSourceKeyOf(field);
  if (!key) return LEAVE;
  // Filed: BMO owns the application, and what is on the screen is the record of
  // what was sent. Note this also covers every locked status the shell already
  // read-onlys the form on, so a locked application is never silently rewritten
  // underneath a banner that says it can no longer be changed.
  if (!isAdvisorWritable(context.status)) return LEAVE;

  const { current } = slot;
  const isEmpty = current === undefined || current === null || current === '';
  if (context.mode === 'preview' && !isEmpty) return LEAVE;

  if (key === 'advisor.repCode') {
    const codes = user.representativeCodes ?? [];
    // One code is not a choice — it is the only code this advisor can file
    // under, so it overwrites a restored code the same way a name would.
    if (codes.length === 1) return { kind: 'set', value: codes[0]! };
    // No codes at all: an originator/approver driving preview, or a failed
    // lookup. Nothing to validate against, so nothing to say.
    if (codes.length === 0 || isEmpty) return LEAVE;
    // A multi-code advisor picked this, and the pick is theirs to keep — but
    // only if it is still one of THEIR codes. A code restored from a draft
    // saved by someone else (or before this advisor's codes changed) is not a
    // choice they ever made, and both the dropdown and the BFF's
    // `ResolveRepCode` assume by construction that any answer here is one of
    // the session's own codes. Emptying it puts the question back to them.
    //
    // No `edited` check needed: the dropdown only ever offers the session's own
    // codes, so a pick made this mount always passes the test above anyway.
    return codes.includes(String(current)) ? LEAVE : { kind: 'clear' };
  }

  const resolved = resolveAdvisorSourceValue(key, user);
  if (resolved === null) return LEAVE;
  // Their correction, made in front of us. Read-only fields can't reach this —
  // they emit no change — so this only ever protects an editable prefill.
  if (slot.edited === true) return LEAVE;
  return resolved === current ? LEAVE : { kind: 'set', value: resolved };
}

/**
 * True when the advisor carries several representative codes and therefore has
 * to choose one (AC3). One code, none, or a non-advisor session all read as
 * false — the field then renders as the plain read-only prefilled input.
 */
export function needsRepCodeChoice(user: MeResponse | null | undefined): boolean {
  return (user?.representativeCodes?.length ?? 0) > 1;
}

/**
 * True when this field is the rep-code slot AND the advisor actually has a
 * choice to make — i.e. it renders as a Select the advisor must answer, not as
 * a prefilled read-only input.
 *
 * Required + read-only fields are normally exempt from the empty check, because
 * the platform supplies their value and an exempt-less gate would block Next
 * while the session resolver is still in flight. This field is the one
 * exception: the resolver deliberately declines to choose (see
 * resolveAdvisorSourceValue), so nothing will ever arrive to fill it and the
 * exemption would let an advisor submit with no rep code at all.
 */
export function isAdvisorRepCodeChoice(
  field: { sourceValue?: string | null },
  user: MeResponse | null | undefined,
): boolean {
  return field.sourceValue === 'advisor.repCode' && needsRepCodeChoice(user);
}

/**
 * Builds the "skip the required-empty check for this field" predicate for a
 * given session. Read-only ⇒ exempt, except for the rep-code picker.
 *
 * A factory rather than a 2-arg predicate because the completeness walkers are
 * module-level pure functions that call it once per field — binding the session
 * once keeps the hot loop free of the extra argument.
 */
export function requiredExemptionFor(
  user: MeResponse | null | undefined,
): (field: { readOnly?: boolean | null; sourceValue?: string | null }) => boolean {
  return (field) => field.readOnly === true && !isAdvisorRepCodeChoice(field, user);
}

/**
 * Stand-in representative codes for `mode='preview'`.
 *
 * Preview is driven by an ORIGINATOR or APPROVER session, which carries no
 * GAMX advisor profile at all (see {@link MeResponse}) — so without a
 * fallback an `advisorRepCodeSelector` field renders an empty, untestable
 * dropdown. Preview is already a sample-data surface (PreviewPage seeds
 * `initialValues` the same way), so sample codes here are consistent with how
 * the rest of the form previews.
 *
 * NEVER used in `mode='advisor'` — a real advisor either has codes from GAMX
 * or never got a session at all (login is blocked on zero codes, see
 * `AuthEndpoints`), so a fabricated code could not silently reach a real save.
 */
export const PREVIEW_ADVISOR_REP_CODES = ['CD5678', 'EF9012', 'GH3456'] as const;

/**
 * The representative codes to offer in an `advisorRepCodeSelector` field's
 * dropdown for this render. Real session codes win whenever there are any;
 * the preview fallback only fires when the session carries none — i.e. a
 * non-advisor session (originator/approver) driving the preview surface.
 */
export function repCodeOptionsFor(
  user: Pick<MeResponse, 'representativeCodes'> | null | undefined,
  mode: 'preview' | 'advisor',
): string[] {
  const codes = user?.representativeCodes ?? [];
  if (codes.length > 0) return codes;
  return mode === 'preview' ? [...PREVIEW_ADVISOR_REP_CODES] : [];
}
